import produce from 'immer';
import { zip } from 'lodash-es';
import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import FluidButton from 'ui/FluidButton';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { StandardModal } from 'pureUi/Modal';
import { iActingOnBehalfOfUser } from 'store/modules/actingOnBehalfOf/model';
import { LiveRatePricesModal } from 'containers/HotelContainer/LiveRatePricesModal';
import { useNotifications } from 'hooks/useNotifications';
import { usePriceCheckAPI } from './hooks/usePriceCheckAPI';
import { BookingBuilderRequest, BookingBuilderResponse, IAccommodation, IHoldCheckResAccommodation, ILiveRatePricesModalItem, IPriceCheckResAccommodation, ITravelAgent, SelectedAccommodation } from 'services/BackendApi';
import { useHoldAPI } from './hooks/useHoldAPI';
import { useHoldCheckAPI } from './hooks/useHoldCheckAPI';
import { IBookingInformation } from 'interfaces';
import { LIVE_RATES_PRICE_CHECK_TOLERANCE_CENTS } from 'config';
import { isOnHold } from './utils';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';
import { HoldCheckModal } from './HoldCheckModal';

interface IHoldButtonProps {
  basketBuild: IBasketBuildL4;
  bookingInformation: IBookingInformation;
  disabled?: boolean;
  selectedTa: ITravelAgent | null;
  actingOnBehalfOfUser?: iActingOnBehalfOfUser | null;
  onSuccess?: (basketBuild: { uuid: string; }) => void;
}

export const HoldButton: React.FC<IHoldButtonProps> = React.memo((props: IHoldButtonProps) => {
  const { isMobile } = useResponsive();
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const effectiveBookingBuilder = isOnHold(props.basketBuild)
      ? props.basketBuild.initialBuildResponse
      : props.basketBuild?.latestBookingBuilderResponse;

  const holdAPI = useHoldAPI({
    basketBuildUuid: props.basketBuild.uuid,
    bookingInformation: props.bookingInformation,
    selectedTa: props.selectedTa,
    actingOnBehalfOfUser: props.actingOnBehalfOfUser,
    clientCountryCode: props.basketBuild.clientCountryCode,
    onSuccess: (basketBuild): void => {
      showSuccessNotification('Successful on-hold request to supplier.');
      if(props.onSuccess) {
        props.onSuccess(basketBuild);
      }
    }
  });

  const [isPriceCheckModalOpen, setPriceCheckModalOpen] = useState(false);
  const [priceCheckModalData, setPriceCheckModalData] = useState<ILiveRatePricesModalItem[] | null>(null);
  const [priceCheckResult, setPriceCheckResult] = useState<IPriceCheckResAccommodation[] | null>(null);
  const [isHoldCheckModalOpen, setHoldCheckModalOpen] = useState(false);
  const [holdCheckFailedItems, setHoldCheckFailedItems] = useState<IHoldCheckResAccommodation[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  const priceCheckAPI = usePriceCheckAPI({
    bookingBuilderRequest: props.basketBuild.buildRequest,
    selectedTa: props.selectedTa,
    actingOnBehalfOfUser: props.actingOnBehalfOfUser,
    clientCountryCode: props.basketBuild.clientCountryCode,
  });

  const holdCheckAPI = useHoldCheckAPI({
    bookingBuilderRequest: props.basketBuild.buildRequest,
    selectedTa: props.selectedTa,
  });

  const handleHoldClick = useCallback(async () => {
    setIsChecking(true);

    const [holdData, priceData] = await Promise.all([
      holdCheckAPI.request(),
      priceCheckAPI.request(),
    ]);

    setIsChecking(false);

    // Hold check errored → error toast, stop (ignore price check)
    if (!holdData) {
      showErrorNotification('Sorry, something went wrong. Please try again.');
      return;
    }

    // Hold check failed (any room can't be held) → informational modal, stop (ignore price check)
    const hasUnavailable = holdData.some(x => !x.canHold);
    if (hasUnavailable) {
      setHoldCheckFailedItems(holdData);
      setHoldCheckModalOpen(true);
      return;
    }

    // Hold check passed → evaluate price check
    if (!priceData) {
      showErrorNotification('Error verifying Live Rate price. Please, try later to complete.');
      return;
    }

    if (!isPriceCheckAcceptable(effectiveBookingBuilder!.potentialBooking.Accommodation, priceData)) {
      setPriceCheckResult(priceData);
      setPriceCheckModalData(buildPriceCheckModalData(effectiveBookingBuilder!, priceData));
      setPriceCheckModalOpen(true);
      return;
    }

    // Both passed → hold silently with updated prices
    holdAPI.request(applyPriceCheck(props.basketBuild.buildRequest, priceData));
  }, [
    holdCheckAPI.request,
    priceCheckAPI.request,
    effectiveBookingBuilder,
    props.basketBuild.buildRequest,
    holdAPI.request,
  ]);

  const handlePriceCheckModalClose = useCallback(() => setPriceCheckModalOpen(false), []);
  const handleHoldCheckModalClose = useCallback(() => setHoldCheckModalOpen(false), []);

  const handlePriceCheckModalConfirm = useCallback(() => {
    const modifiedBookingBuilderRequest = applyPriceCheck(props.basketBuild.buildRequest, priceCheckResult!);
    holdAPI.request(modifiedBookingBuilderRequest);
  }, [props.basketBuild.buildRequest, priceCheckResult]);

  const disabled = props.disabled || isChecking || holdAPI.isLoading;

  const holdCheckModalItems = buildHoldCheckModalItems(
    holdCheckFailedItems,
    effectiveBookingBuilder?.potentialBooking.Accommodation ?? [],
  );

  return (
    <div className="hold-container">
      <FluidButton
        type="primary"
        className={classnames(
          'hold-button flex-1 w-full font-semibold font-hurmegeometric-sans min-h-[55px] my-[14px] rounded-none uppercase',
          { 'hover:bg-green-prime hover:border-none': !disabled }
        )}
        disabled={disabled}
        onClick={handleHoldClick}
        isLoading={isChecking || holdAPI.isLoading}
      >
        Hold
      </FluidButton>
      {isHoldCheckModalOpen && (
        isMobile ? (
          <MobileBottomDrawer
            isOpen={isHoldCheckModalOpen}
            onClose={handleHoldCheckModalClose}
          >
            <HoldCheckModal
              items={holdCheckModalItems}
              onClose={handleHoldCheckModalClose}
            />
          </MobileBottomDrawer>
        ) : (
          <StandardModal removePadding={true} showCloseButton={false}>
            <HoldCheckModal
              items={holdCheckModalItems}
              onClose={handleHoldCheckModalClose}
            />
          </StandardModal>
        )
      )}
      {isPriceCheckModalOpen && (
        isMobile ? (
          <MobileBottomDrawer
            isOpen={isPriceCheckModalOpen}
            onClose={handlePriceCheckModalClose}
            title="Changes in rates"
            contentClassName="[&>*]:min-w-0 [&>*]:max-w-full"
          >
            <LiveRatePricesModal
              confirmButtonLabel="Confirm"
              onConfirm={handlePriceCheckModalConfirm}
              onClose={handlePriceCheckModalClose}
              isConfirmDisabled={disabled}
              liveRates={priceCheckModalData!}
              currency={effectiveBookingBuilder!.currency}
              total={effectiveBookingBuilder!.totals?.totalForPricedItemsCents ?? 0}
            />
          </MobileBottomDrawer>
        ) : (
          <StandardModal removePadding={true} showCloseButton={false}>
            <LiveRatePricesModal
              confirmButtonLabel="Confirm"
              onConfirm={handlePriceCheckModalConfirm}
              onClose={handlePriceCheckModalClose}
              isConfirmDisabled={disabled}
              liveRates={priceCheckModalData!}
              currency={effectiveBookingBuilder!.currency}
              total={effectiveBookingBuilder!.totals?.totalForPricedItemsCents ?? 0}
            />
          </StandardModal>
        )
      )}
    </div>
  );
});

function buildPriceCheckModalData(
  bookingBuilder: BookingBuilderResponse,
  priceCheckAccoms: IPriceCheckResAccommodation[]
): ILiveRatePricesModalItem[] {
  const origAccom = bookingBuilder.potentialBooking.Accommodation;

  return priceCheckAccoms.map((priceCheckAccom, index) => ({
      title: origAccom[index]?.product.name,
      liveRate: priceCheckAccom.liveRate,
      guestAges: priceCheckAccom.guestAges,
      prevPrice: origAccom[index]?.totalCents ?? 0,
      newPrice: priceCheckAccom.priceCheck?.totalCents ?? 0,
      prevMealPlan: origAccom[index]?.subProducts?.['Meal Plan']?.[0].product.name ?? '',
      newMealPlan: priceCheckAccom.priceCheck?.externalMealPlanDescription ?? '',
  }));
}

function applyPriceCheck(
  bookingBuilderRequest: BookingBuilderRequest,
  priceChecks: IPriceCheckResAccommodation[]
): BookingBuilderRequest {
  return produce(bookingBuilderRequest, (draft: BookingBuilderRequest) => {
    const ts = zip(bookingBuilderRequest.Accommodation, priceChecks);
    const defined = ts.filter(t => t[1]?.priceCheck);

    draft.Accommodation = defined.map(([acc, res]) => {
      acc!.liveRate!.amount = res!.priceCheck!.totalCents;
      acc!.liveRate!.externalMealPlanCode = res!.priceCheck!.externalMealPlanCode;
      return acc;
    }) as SelectedAccommodation[];

    return draft;
  });
}
function isPriceCheckAcceptable(buildAccomodations: IAccommodation[], priceCheckAccommodations: IPriceCheckResAccommodation[]): boolean {
  return buildAccomodations.every((acc, i) => {
    const origValue = buildAccomodations[i].totalCents;
    const priceCheckValue = priceCheckAccommodations[i]?.priceCheck?.totalCents || 0;
    return Math.abs(origValue - priceCheckValue) <= LIVE_RATES_PRICE_CHECK_TOLERANCE_CENTS;
  });
}

function buildHoldCheckModalItems(
  holdCheckAccommodations: IHoldCheckResAccommodation[],
  bookingAccommodations: IAccommodation[],
) {
  return holdCheckAccommodations
    .map((accom, index) => ({
      canHold: accom.canHold,
      roomName: bookingAccommodations[index]?.product.name ?? 'Unknown Room',
      startDate: accom.startDate,
      endDate: accom.endDate,
      guestAges: accom.guestAges,
    }))
    .filter(item => !item.canHold);
}
